<script setup lang="ts">
import { onBeforeUnmount, reactive, ref, watch } from 'vue'
import type { ExchangeSoftwareItem } from '~/types/exchange-software-module'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    items?: ExchangeSoftwareItem[]
    title?: string
    totalDownloadsLabel?: string
  }>(),
  {
    items: () => [],
    title: '',
    totalDownloadsLabel: '',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'software-detail': [software: ExchangeSoftwareItem]
}>()

const { locale } = useAppI18n()
const { lock: lockBackgroundScroll, unlock: unlockBackgroundScroll } = useStickyDialogScrollLock()

const previewVisible = ref(false)
const previewImages = ref<string[]>([])
const previewInitialIndex = ref(0)

// 拖拽截图时记录起点；移动超过阈值后抑制随后的 click，避免误触图片预览。
const dragState = reactive({
  active: false,
  moved: false,
  startX: 0,
  startScrollLeft: 0,
  target: null as HTMLElement | null,
})

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      lockBackgroundScroll()
      return
    }

    previewVisible.value = false
    unlockBackgroundScroll()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  unlockBackgroundScroll()
})

const normalizeText = (value: unknown, fallback = '') => {
  if (value === null || value === undefined) return fallback
  const text = String(value).trim()
  return text || fallback
}

const toRecord = (value: unknown): Record<string, unknown> => {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

const toArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value
  const record = toRecord(value)
  if (Array.isArray(record.items)) return record.items
  if (Array.isArray(record.list)) return record.list
  if (Array.isArray(record.result)) return record.result
  return []
}

/** 兼容字符串数组和旧接口的对象数组截图结构。 */
const normalizeImageList = (value: unknown) => {
  const values = Array.isArray(value) ? value : toArray(value)
  return values
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      const record = toRecord(item)
      return normalizeText(record.url || record.src || record.image || record.webImage)
    })
    .filter(Boolean)
}

/** 下载量统一按当前语言格式化，非数字内容保持接口原值。 */
const formatNumber = (value: unknown) => {
  const text = normalizeText(value)
  if (!text) return '--'
  const numeric = Number(text)
  if (!Number.isFinite(numeric)) return text
  return new Intl.NumberFormat(locale).format(numeric)
}

/** 关闭完整交易应用列表。 */
const closeDialog = () => {
  previewVisible.value = false
  emit('update:modelValue', false)
}

/** 关闭弹层并将软件详情点击交给父组件处理。 */
const openSoftwareDetail = (software: ExchangeSoftwareItem) => {
  closeDialog()
  emit('software-detail', software)
}

/** 打开当前软件的指定截图。 */
const openPreview = (software: ExchangeSoftwareItem, index: number) => {
  const images = normalizeImageList(software.screenshots)
  if (!images.length) return
  previewImages.value = images
  previewInitialIndex.value = Math.max(0, Math.min(index, images.length - 1))
  previewVisible.value = true
}

const startGalleryDrag = (event: PointerEvent) => {
  const target = event.currentTarget as HTMLElement | null
  if (!target) return
  dragState.active = true
  dragState.moved = false
  dragState.startX = event.clientX
  dragState.startScrollLeft = target.scrollLeft
  dragState.target = target
  target.setPointerCapture?.(event.pointerId)
}

const moveGalleryDrag = (event: PointerEvent) => {
  if (!dragState.active || !dragState.target) return
  const distance = event.clientX - dragState.startX
  if (Math.abs(distance) > 4) dragState.moved = true
  dragState.target.scrollLeft = dragState.startScrollLeft - distance
}

const stopGalleryDrag = (event?: PointerEvent) => {
  if (dragState.target && event?.pointerId !== undefined) {
    dragState.target.releasePointerCapture?.(event.pointerId)
  }
  dragState.active = false
  dragState.target = null
  if (dragState.moved) {
    window.setTimeout(() => {
      dragState.moved = false
    }, 0)
  }
}

/** 拖拽结束后的 click 只用于收尾，不打开图片预览。 */
const shouldOpenPreview = (software: ExchangeSoftwareItem, index: number, event: MouseEvent) => {
  if (dragState.moved) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  openPreview(software, index)
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="props.modelValue"
      class="fixed inset-0 z-[2090] flex items-center justify-center bg-[#00000099] p-[16px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exchange-software-dialog-title"
      tabindex="-1"
      @click.self="closeDialog"
      @keydown.esc="closeDialog"
    >
      <div class="relative flex w-[594px] flex-col rounded-[14px] bg-[#fff] p-[32px]">
        <button
          type="button"
          class="absolute top-[20px] right-[20px] z-[1] flex size-[24px] cursor-pointer items-center justify-center rounded-full text-[24px] leading-none text-[#676c73] transition hover:bg-[#f2f3f5] rtl:right-auto rtl:left-[20px]"
          aria-label="关闭交易应用弹层"
          @click="closeDialog"
        >
          <span aria-hidden="true">×</span>
        </button>

        <div class="mb-[20px] flex shrink-0 items-center gap-[12px]">
          <span class="h-[20px] w-[6px] shrink-0 rounded-[1px] bg-[#ffd733]" aria-hidden="true"></span>
          <h2 id="exchange-software-dialog-title" class="m-0 text-[20px] leading-[28px] font-[600] text-[#1d2129]">
            {{ props.title }}
          </h2>
        </div>

        <div class="flex h-[600px] shrink-0 flex-col gap-[20px] overflow-y-auto">
          <article
            v-for="(software, softwareIndex) in props.items"
            :key="[normalizeText(software.softwareId), softwareIndex].join('-')"
            class="flex w-full shrink-0 cursor-pointer flex-col gap-[12px] rounded-[8px] bg-[#fafafa] p-[16px] transition hover:bg-[#f2f3f5]"
            @click="openSoftwareDetail(software)"
          >
            <div class="flex min-w-0 items-start justify-between gap-[12px]">
              <div class="flex min-w-0 items-center gap-[10px]">
                <div class="relative size-[38px] shrink-0 overflow-hidden rounded-[8px]">
                  <CommonImage
                    :src="normalizeText(software.logo)"
                    :alt="normalizeText(software.name, 'software')"
                    width="38"
                    height="38"
                    class="size-full border border-solid border-[#ededed] object-cover"
                    loading="lazy"
                  />
                  <span
                    class="absolute top-0 right-0 z-[2] flex size-[14px] items-center justify-center rounded-tr-[7px] rounded-bl-[7px] bg-[rgba(0,0,0,0.4)] rtl:right-auto rtl:left-0 rtl:rounded-tl-[7px] rtl:rounded-tr-none rtl:rounded-br-[7px] rtl:rounded-bl-none"
                  >
                    <CommonImage
                      v-if="Number(software.marketId) === 1"
                      src="https://resources1.interface003.com/web30/2026/09/16/714da195-1b95-4a1e-a13b-be29ee4c7347.png"
                      width="10"
                      height="10"
                      loading="eager"
                    />
                    <CommonImage
                      v-else
                      src="https://resources1.interface003.com/web30/2026/09/16/c97c0119-376e-4e44-b55c-e7c1029705cd.png"
                      width="10"
                      height="10"
                      loading="eager"
                    />
                  </span>
                </div>
                <div class="flex min-w-0 flex-col gap-[2px]">
                  <div class="truncate text-[16px] leading-[24px] font-[500] text-[#231c1c]">
                    {{ normalizeText(software.name, '--') }}
                  </div>
                  <div class="truncate text-[12px] leading-[18px] text-[#86909c]">
                    {{ normalizeText(software.title || software.developerName, '--') }}
                  </div>
                </div>
              </div>
              <div class="flex shrink-0 flex-col items-end gap-[4px] rtl:items-start">
                <span class="text-[12px] leading-[18px] text-[#86909c]">{{ props.totalDownloadsLabel }}</span>
                <span class="text-[12px] leading-[18px] font-[500] text-[#3d3d3d]">
                  {{ formatNumber(software.downloadNum) }}
                </span>
              </div>
            </div>

            <div class="border border-dashed border-[#e5e6eb]" aria-hidden="true"></div>

            <div
              v-if="normalizeImageList(software.screenshots).length"
              class="flex cursor-grab gap-[12px] overflow-x-auto active:cursor-grabbing"
              style="scrollbar-width: none"
              @pointerdown.stop="startGalleryDrag"
              @pointermove.stop="moveGalleryDrag"
              @pointerup.stop="stopGalleryDrag"
              @pointercancel.stop="stopGalleryDrag"
              @pointerleave.stop="stopGalleryDrag"
              @click.stop
            >
              <button
                v-for="(image, imageIndex) in normalizeImageList(software.screenshots)"
                :key="[image, imageIndex].join('-')"
                type="button"
                class="block h-[160px] min-w-[74px] cursor-pointer overflow-hidden rounded-[8px] transition hover:opacity-85"
                @click="shouldOpenPreview(software, imageIndex, $event)"
              >
                <CommonImage
                  :src="image"
                  :alt="normalizeText(software.name, 'software') + ' screenshot ' + (imageIndex + 1)"
                  width="74"
                  height="160"
                  class="size-full object-cover"
                  loading="lazy"
                />
              </button>
            </div>
          </article>
        </div>
      </div>
    </div>

    <CommonImagePreviewDialog v-model="previewVisible" :images="previewImages" :initial-index="previewInitialIndex" />
  </Teleport>
</template>
